import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, ViewChild, ElementRef, HostListener, Inject } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { DataGridResponse } from '../../../utils/interfaces';
import { RunView, LogStatus, LogError, Metadata, RunQuery } from '@memberjunction/core';
import { NotificationService } from '../../../services/notification.service';
import { UserCommitteeAccessService } from '../../../services/user-committee-access.service';
import { ActiveUserService } from '../../../services/active-user.service';
import { UserAuthService } from '../../../services/user-auth.service';
import {
  DataGridColumn,
  DataGridSort,
  DataGridFilter,
  DataGridExportOptions,
  ServerSideFilter,
  ServerSideFilterGroup,
  DistinctValue,
  ColumnFilterCache,
  DropdownFilterState
} from '../../../utils/interfaces';
import { QueryNames } from 'src/app/utils/constants';

// Extend jsPDF type for autoTable
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
  }
}




@Component({
  selector: 'acs-data-grid',
  templateUrl: './acs-data-grid.html',
  styleUrls: ['./acs-data-grid.scss']
})
export class AcsDataGridComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLDivElement>;

  // Existing client-side properties
  @Input() data: any[] = [];
  @Input() columns: DataGridColumn[] = [];
  @Input() loading: boolean = false;
  @Input() loadingText: string = 'Loading...';
  @Input() emptyText: string = 'No data available';
  @Input() pageSize: number = 50;
  @Input() virtualScrolling: boolean = true;
  @Input() infiniteScroll: boolean = false;
  @Input() selectable: 'none' | 'single' | 'multiple' = 'none';
  @Input() sortable: boolean = true;
  @Input() filterable: boolean = true;
  @Input() exportable: boolean = false;
  @Input() searchable: boolean = true;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() viewMode: 'grid' | 'card' = 'grid';
  @Input() cardTemplate: any = null; // TemplateRef for custom card layout
  @Input() rowHeight: number = 48;
  @Input() stickyHeader: boolean = true;
  @Input() resizableColumns: boolean = true;
  @Input() exportFilenamePrefix: string = 'data-export';
  @Input() truncateText: boolean = false; // Option to truncate text with ellipsis (default is wrap)
  @Input() showTooltips: boolean = true; // Show full text in tooltips when truncated
  @Input() maxLines: number = 2; // Maximum lines before showing ellipsis
  @Input() clampLines: boolean = true; // Enable line clamping with ellipsis after maxLines

  /**
   * Enable dropdown filter functionality for columns with dropdownFilter: true.
   * When enabled, columns with dropdownFilter property will show a dropdown instead of text input.
   *
   * @default false
   * @example
   * ```html
   * <acs-data-grid [enableDropdownFilters]="true" [data]="myData" [columns]="myColumns">
   * </acs-data-grid>
   * ```
   */
  @Input() enableDropdownFilters: boolean = false;

  /**
   * Cache timeout for dropdown filter distinct values in milliseconds.
   * Cached values are automatically invalidated when other filters change.
   *
   * @default 300000 (5 minutes)
   * @example
   * ```html
   * <acs-data-grid [dropdownFilterCacheTimeout]="600000" [enableDropdownFilters]="true">
   * </acs-data-grid>
   * ```
   */
  @Input() dropdownFilterCacheTimeout: number = 300000;

  // New server-side properties
  @Input() serverSide: boolean = false;
  @Input() entityName: string = '';
  @Input() totalCount: number = 0;
  @Input() currentPage: number = 1;
  @Input() showPagination: boolean = true;
  @Input() showPageSizeSelector: boolean = true;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100, 250];
  @Input() serverSideFilters: (ServerSideFilter | ServerSideFilterGroup)[] = [];
  @Input() autoLoad: boolean = true;

  // Committee access filtering
  @Input() enableCommitteeFiltering: boolean = true; // Enable/disable committee access filtering
  @Input() committeeColumnName: string = 'CommitteeID'; // Column name for committee/chapter filtering

  @Output() sort = new EventEmitter<DataGridSort>();
  @Output() filter = new EventEmitter<DataGridFilter[]>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowDoubleClick = new EventEmitter<any>();
  @Output() export = new EventEmitter<DataGridExportOptions>();
  @Output() loadMore = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() serverDataLoaded = new EventEmitter<DataGridResponse<any>>();
  @Output() serverError = new EventEmitter<any>();

  private subscriptions = new Subscription();

  // Internal state
  filteredData: any[] = [];
  displayedData: any[] = [];
  selectedItems: Set<any> = new Set();
  currentSort: DataGridSort | null = null;
  currentFilters: DataGridFilter[] = [];
  scrollPosition: number = 0;
  visibleStartIndex: number = 0;
  visibleEndIndex: number = 0;
  containerHeight: number = 600; // Fixed container height - do not change during scroll
  currentClientHeight: number = 600; // Dynamic height from scroll events
  itemHeight: number = 48;

  // Dropdown filter state
  distinctValueCache: Map<string, ColumnFilterCache> = new Map();
  dropdownStates: Map<string, DropdownFilterState> = new Map();
  activeDropdownFilters: Map<string, any> = new Map();

  // Server-side state
  serverResponse: DataGridResponse<any> | null = null;
  serverLoading: boolean = false;
  serverErrorMessage: string | null = null;
  totalPages: number = 1;
  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;
  
  // MJ recommended: StartRow tracking instead of page-based calculation
  currentStartRow: number = 0;
  allLoadedData: any[] = []; // Track all accumulated data
  lastResponseRowCount: number = 0; // Track actual RowCount from last response

  // Filter states
  showFilterRow: boolean = false;
  filterValues: { [key: string]: any } = {};
  searchValue: string = '';
  showExportMenu: boolean = false;

  // Simple export progress tracking
  isExporting: boolean = false;
  exportProgressMessage: string = '';

  // Resize states
  resizing: { column: string; startX: number; startWidth: number } | null = null;
  
  // Store bound scroll function for proper cleanup
  private boundScrollHandler = this.onScroll.bind(this);


  constructor(
    private notificationService: NotificationService,
    private userCommitteeAccessService: UserCommitteeAccessService,
    private activeUserService: ActiveUserService,
    private userAuthService: UserAuthService
  ) {
    // Extend dayjs with UTC plugin for timezone-independent date handling
    dayjs.extend(utc);
  }

  ngOnInit(): void {
    this.itemHeight = this.rowHeight;

    if (this.serverSide) {
      // Initialize server-side mode
      this.initializeServerSide();

      // Pre-load dropdown filters for server-side mode (data is fetched by grid)
      if (this.enableDropdownFilters) {
        setTimeout(() => this.preloadDropdownFilters(), 100);
      }
    } else {
      // Initialize client-side mode (existing behavior)
      this.processData();

      // For client-side mode, dropdown filters will be loaded when data arrives via ngOnChanges
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if data changed for client-side mode
    if (changes['data'] && !this.serverSide) {
      this.processData();

      // Reload dropdown filters when data changes (including first load)
      if (this.enableDropdownFilters && this.data && this.data.length > 0) {
        setTimeout(() => this.preloadDropdownFilters(), 100);
      }
      return;
    }

    // Check if any of the critical inputs have changed after initial load
    const isInitialized = !changes['columns']?.firstChange || 
                         !changes['serverSide']?.firstChange || 
                         !changes['entityName']?.firstChange || 
                         !changes['serverSideFilters']?.firstChange;

    if (isInitialized) {
      let shouldReload = false;

      // Check if columns changed
      if (changes['columns'] && !changes['columns'].firstChange) {
        shouldReload = true;
      }

      // Check if serverSide mode changed
      if (changes['serverSide'] && !changes['serverSide'].firstChange) {
        shouldReload = true;
        // Reset relevant state when switching modes
        this.currentPage = 1;
        this.currentStartRow = 0;
        this.allLoadedData = [];
        this.displayedData = [];
      }

      // Check if entityName changed (only relevant for server-side mode)
      if (changes['entityName'] && !changes['entityName'].firstChange && this.serverSide) {
        shouldReload = true;
        // Reset pagination and sorting when entity changes
        this.currentPage = 1;
        this.currentStartRow = 0;
        this.allLoadedData = [];
        this.displayedData = [];
        this.currentSort = null;
        this.currentFilters = [];
        this.filterValues = {};
      }

      // Check if serverSideFilters changed
      if (changes['serverSideFilters'] && !changes['serverSideFilters'].firstChange && this.serverSide) {
        shouldReload = true;
        // Note: serverSideFilters are now properly typed and handled in buildServerSideFilterSQL()
        // No need to copy them to currentFilters as they're separate concerns
        // Reset pagination when filters change
        this.currentPage = 1;
        this.currentStartRow = 0;
        this.allLoadedData = [];
        this.displayedData = [];
        // Also clear sort when serverSideFilters change (likely entity change)
        this.currentSort = null;
      }

      // Reload data if necessary
      if (shouldReload) {
        // Set appropriate loading state
        if (this.serverSide) {
          this.serverLoading = true;
          // Reload server data with new configuration
          this.loadServerData();
        } else {
          this.loading = true;
          // Reprocess client-side data
          this.processData();
          // Clear loading state for client-side (synchronous operation)
          this.loading = false;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    // Setup scroll listener after view is initialized
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    if (this.resizing) {
      document.removeEventListener('mousemove', this.onColumnResize);
      document.removeEventListener('mouseup', this.onColumnResizeEnd);
    }
    
    // Remove scroll listener
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.removeEventListener('scroll', this.boundScrollHandler);
    }
    
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.showExportMenu && !target.closest('.acs-data-grid__export')) {
      this.showExportMenu = false;
    }
  }

  private processData(): void {
    this.filteredData = this.applyFilters(this.data);
    this.applySorting();
    this.updateDisplayedData();
  }

  private applyFilters(data: any[]): any[] {
    let filteredData = data;

    // Apply global search filter
    if (this.searchValue.trim()) {
      const searchTerm = this.searchValue.toLowerCase();
      filteredData = filteredData.filter(item => {
        return this.columns.some(column => {
          const value = item[column.key];
          return String(value || '').toLowerCase().includes(searchTerm);
        });
      });
    }

    // Apply column-specific filters
    if (this.currentFilters.length > 0) {
      filteredData = filteredData.filter(item => {
        return this.currentFilters.every(filter => {
          const value = item[filter.column];
          const filterValue = filter.value;

          if (filterValue === null || filterValue === undefined || filterValue === '') {
            return true;
          }

          switch (filter.operator || 'contains') {
            case 'equals':
              return value == filterValue;
            case 'contains':
              return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
            case 'startsWith':
              return String(value).toLowerCase().startsWith(String(filterValue).toLowerCase());
            case 'endsWith':
              return String(value).toLowerCase().endsWith(String(filterValue).toLowerCase());
            case 'greaterThan':
              return Number(value) > Number(filterValue);
            case 'lessThan':
              return Number(value) < Number(filterValue);
            default:
              return true;
          }
        });
      });
    }

    return filteredData;
  }

  private applySorting(): void {
    if (!this.currentSort) return;

    const { column, direction } = this.currentSort;
    const columnConfig = this.columns.find(col => col.key === column);

    this.filteredData.sort((a, b) => {
      let valueA = a[column];
      let valueB = b[column];

      // Handle different data types
      if (columnConfig?.type === 'number') {
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
      } else if (columnConfig?.type === 'date') {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      } else {
        valueA = String(valueA || '').toLowerCase();
        valueB = String(valueB || '').toLowerCase();
      }

      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  private updateDisplayedData(): void {
    if (this.serverSide) {
      // Server-side mode: displayedData is managed by server response
      // Virtual scrolling not needed since server controls pagination
      return;
    }
    
    if (this.virtualScrolling) {
      this.calculateVisibleRange();
      const start = Math.max(0, this.visibleStartIndex - 5); // Buffer
      const end = Math.min(this.filteredData.length, this.visibleEndIndex + 5);
      this.displayedData = this.filteredData.slice(start, end);
    } else {
      // Show all data when virtual scrolling is disabled
      this.displayedData = this.filteredData;
    }
  }

  private calculateVisibleRange(): void {
    const scrollTop = this.scrollPosition;
    const containerHeight = this.currentClientHeight; // Use dynamic height for calculations
    const dataLength = this.serverSide ? this.displayedData.length : this.filteredData.length;
    
    this.visibleStartIndex = Math.floor(scrollTop / this.itemHeight);
    this.visibleEndIndex = Math.min(
      dataLength,
      Math.ceil((scrollTop + containerHeight) / this.itemHeight)
    );
  }

  private setupScrollListener(): void {
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.addEventListener('scroll', this.boundScrollHandler);
    }
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.scrollPosition = target.scrollTop;
    this.currentClientHeight = target.clientHeight;

    if (this.virtualScrolling) {
      this.updateDisplayedData();
    }

    // Scroll to bottom check - handle both infinite scroll and auto-load next page
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 100;
    
    if (isNearBottom && this.serverSide && this.hasNextPage && !this.serverLoading) {
      if (this.infiniteScroll) {
        // True infinite scroll - load more data and append
        this.loadServerData();
      } else {
        // Auto-load next page on scroll to bottom (lazy loading pagination)
        this.goToNextPage();
      }
    } else if (isNearBottom && !this.serverSide && this.infiniteScroll) {
      // Client-side infinite scroll - emit loadMore event
      this.loadMore.emit();
    }
  }

  onSort(column: DataGridColumn): void {
    if (!column.sortable || !this.sortable) return;

    if (this.serverSide) {
      this.onServerSort(column);
    } else {
      // Existing client-side logic
      const newSort: DataGridSort = {
        column: column.key,
        direction: this.currentSort?.column === column.key && this.currentSort?.direction === 'asc' ? 'desc' : 'asc'
      };

      this.currentSort = newSort;
      this.sort.emit(newSort);
      this.applySorting();
      this.updateDisplayedData();
    }
  }

  onFilter(column: string, value: any): void {
    if (this.serverSide) {
      this.onServerFilter(column, value);
    } else {
      // Set loading state while filter is being applied
      this.loading = true;

      // Existing client-side logic
      const existingFilterIndex = this.currentFilters.findIndex(f => f.column === column);

      if (value === null || value === undefined || value === '') {
        if (existingFilterIndex >= 0) {
          this.currentFilters.splice(existingFilterIndex, 1);
        }
      } else {
        // Check if this is a dropdown filter column
        const columnDef = this.columns.find(col => col.key === column);
        const isDropdownFilter = columnDef?.dropdownFilter === true;

        // Use 'equals' operator for dropdown filters, 'contains' for text filters
        const filter: DataGridFilter = {
          column,
          value,
          operator: isDropdownFilter ? 'equals' : 'contains'
        };

        if (existingFilterIndex >= 0) {
          this.currentFilters[existingFilterIndex] = filter;
        } else {
          this.currentFilters.push(filter);
        }
      }

      this.filter.emit(this.currentFilters);

      // Use setTimeout to allow loading indicator to show before processing
      setTimeout(() => {
        this.processData();
        this.loading = false;
      }, 0);
    }
  }

  toggleFilterRow(): void {
    this.showFilterRow = !this.showFilterRow;
  }

  onSearch(value: string): void {
    if (this.serverSide) {
      this.onServerSearch(value);
    } else {
      // Existing client-side logic
      this.searchValue = value;
      this.processData();
    }
  }

  clearSearch(): void {
    this.searchValue = '';
    this.processData();
  }

  /**
   * Check if there are any active filters
   */
  hasActiveFilters(): boolean {
    return this.currentFilters.length > 0 || !!this.searchValue;
  }

  clearFilters(): void {
    if (this.serverSide) {
      this.clearServerFilters();
    } else {
      // Existing client-side logic
      this.currentFilters = [];
      this.filterValues = {};
      this.searchValue = '';
      this.filter.emit(this.currentFilters);
      this.processData();
    }
  }

  onRowClick(item: any, event: Event | MouseEvent): void {
    if (this.selectable !== 'none') {
      this.toggleSelection(item, event);
    }
    this.rowClick.emit(item);
  }

  onRowDoubleClick(item: any): void {
    this.rowDoubleClick.emit(item);
  }

  toggleSelection(item: any, event?: Event | MouseEvent): void {
    if (this.selectable === 'none') return;

    if (this.selectable === 'single') {
      this.selectedItems.clear();
      this.selectedItems.add(item);
    } else if (this.selectable === 'multiple') {
      // Check if this is a checkbox event (change event) or a click event
      const isCheckboxEvent = event && event.type === 'change';
      
      if (isCheckboxEvent) {
        // For checkbox changes, simply toggle the item
        if (this.selectedItems.has(item)) {
          this.selectedItems.delete(item);
        } else {
          this.selectedItems.add(item);
        }
      } else if ((event as MouseEvent)?.ctrlKey || (event as MouseEvent)?.metaKey) {
        // Ctrl/Cmd+click for row clicks
        if (this.selectedItems.has(item)) {
          this.selectedItems.delete(item);
        } else {
          this.selectedItems.add(item);
        }
      } else if ((event as MouseEvent)?.shiftKey && this.selectedItems.size > 0) {
        // Shift+click range selection logic here
        this.handleRangeSelection(item);
      } else {
        // Regular row click - clear selection and select only this item
        this.selectedItems.clear();
        this.selectedItems.add(item);
      }
    }

    this.selectionChange.emit(Array.from(this.selectedItems));
  }

  private handleRangeSelection(item: any): void {
    // Simple range selection implementation
    const lastSelected = Array.from(this.selectedItems)[this.selectedItems.size - 1];
    const lastIndex = this.filteredData.indexOf(lastSelected);
    const currentIndex = this.filteredData.indexOf(item);
    
    if (lastIndex >= 0 && currentIndex >= 0) {
      const start = Math.min(lastIndex, currentIndex);
      const end = Math.max(lastIndex, currentIndex);
      
      for (let i = start; i <= end; i++) {
        this.selectedItems.add(this.filteredData[i]);
      }
    }
  }

  selectAll(): void {
    if (this.selectable === 'multiple') {
      this.selectedItems.clear();
      // For server-side grids, use displayedData, for client-side use filteredData
      const dataToSelect = this.serverSide ? this.displayedData : this.filteredData;
      dataToSelect.forEach(item => this.selectedItems.add(item));
      this.selectionChange.emit(Array.from(this.selectedItems));
    }
  }

  clearSelection(): void {
    this.selectedItems.clear();
    this.selectionChange.emit([]);
  }

  isSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  get allSelected(): boolean {
    // For server-side grids, use displayedData, for client-side use filteredData
    const dataToCheck = this.serverSide ? this.displayedData : this.filteredData;
    return dataToCheck.length > 0 && this.selectedItems.size === dataToCheck.length;
  }

  get someSelected(): boolean {
    // For server-side grids, use displayedData, for client-side use filteredData
    const dataToCheck = this.serverSide ? this.displayedData : this.filteredData;
    return this.selectedItems.size > 0 && this.selectedItems.size < dataToCheck.length;
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'card' : 'grid';
  }

  toggleExportMenu(): void {
    this.showExportMenu = !this.showExportMenu;
  }

  onExport(format: 'csv' | 'excel' | 'pdf'): void {
    const exportOptions: DataGridExportOptions = {
      format,
      filename: `${this.exportFilenamePrefix}-${new Date().toISOString().split('T')[0]}`,
      includeHeaders: true,
      selectedOnly: this.selectedItems.size > 0
    };

    this.showExportMenu = false;

    // Handle export internally first
    this.performExport(exportOptions);

    // Also emit for parent components that might want to handle it
    this.export.emit(exportOptions);
  }

  private async performExport(options: DataGridExportOptions): Promise<void> {
    // Simple client-side export only
    await this.performDirectExport(options);
  }

 


  private async performDirectExport(options: DataGridExportOptions): Promise<void> {
    try {
      // Show initial progress
      this.isExporting = true;
      this.exportProgressMessage = `Preparing ${options.format.toUpperCase()} export...`;

      // Get data to export
      let dataToExport: any[];

      if (options.selectedOnly && this.selectedItems.size > 0) {
        // Export selected items only
        dataToExport = Array.from(this.selectedItems);
        this.exportProgressMessage = `Exporting ${dataToExport.length} selected rows...`;
      } else {
        // Export ALL data (not just what's loaded in grid)
        this.exportProgressMessage = 'Fetching complete dataset for export...';
        dataToExport = await this.fetchCompleteDataForExport();
      }

      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }

      // Show dataset size info
      LogStatus(`Exporting ${dataToExport.length} rows to ${options.format.toUpperCase()}`);

      // For very large datasets, give user a heads up
      if (dataToExport.length > 10000) {
        const confirmed = confirm(
          `You are about to export ${dataToExport.length.toLocaleString()} rows. This may take several minutes for PDF generation. Continue?`
        );
        if (!confirmed) return;
      }

      // Small delay to allow UI to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Update progress message
      this.exportProgressMessage = `Generating ${options.format.toUpperCase()} file with ${dataToExport.length.toLocaleString()} rows...`;

      // Perform the export based on format
      switch (options.format) {
        case 'csv':
          await this.exportToCSV(dataToExport, options);
          break;
        case 'excel':
          await this.exportToExcel(dataToExport, options);
          break;
        case 'pdf':
          await this.exportToPDF(dataToExport, options);
          break;
      }

      // Success notification
      this.notificationService.success(
        'Export Complete',
        `Successfully exported ${dataToExport.length.toLocaleString()} rows to ${options.format.toUpperCase()}`
      );

    } catch (error) {
      LogError('Export failed:', undefined, error);
      this.notificationService.error(
        'Export Failed',
        `Failed to export data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      // Hide progress indicator
      this.isExporting = false;
      this.exportProgressMessage = '';
    }
  }

  /**
   * Fetch complete dataset for export (not just grid's loaded subset)
   */
  private async fetchCompleteDataForExport(): Promise<any[]> {
    if (!this.serverSide) {
      // Client-side: return all filtered data
      return this.filteredData;
    }

    // Server-side: fetch ALL records efficiently
    return await this.fetchAllServerData();
  }

  /**
   * Efficiently fetch all server data for export using batched requests
   */
  private async fetchAllServerData(): Promise<any[]> {
    if (!this.entityName) {
      throw new Error('Entity name is required for server-side export');
    }

    const batchSize = 5000; // Fetch in batches of 5k records
    let allData: any[] = [];
    let currentOffset = 0;
    let hasMoreData = true;
    let batchNumber = 1;

    const rv = new RunView();

    // Build filter SQL (same as grid uses)
    const extraFilter = this.buildCompleteFilterSQL();

    // Build OrderBy string (same as grid uses)
    let orderBy = '';
    if (this.currentSort) {
      orderBy = `${this.currentSort.column} ${this.currentSort.direction.toUpperCase()}`;
    }

    while (hasMoreData) {
      this.exportProgressMessage = `Fetching data batch ${batchNumber} (${allData.length.toLocaleString()} records loaded)...`;

      try {
        const result = await rv.RunView({
          EntityName: this.entityName,
          ExtraFilter: extraFilter || undefined,
          OrderBy: orderBy || undefined,
          StartRow: currentOffset,
          MaxRows: batchSize,
          ResultType: 'entity_object'
        });

        if (result.Success && result.Results) {
          const batchData = result.Results;
          allData = allData.concat(batchData);

          // Check if we got less than requested (means we're at the end)
          hasMoreData = batchData.length === batchSize;
          currentOffset += batchSize;
          batchNumber++;

          // Safety check to prevent infinite loops
          if (allData.length > 100000) {
            LogStatus(`Export reached safety limit of 100k records. Stopping at ${allData.length} records.`);
            break;
          }
        } else {
          throw new Error(result.ErrorMessage || 'Failed to fetch data batch');
        }
      } catch (error) {
        LogError(`Failed to fetch batch ${batchNumber}:`, undefined, error);
        throw new Error(`Failed to fetch data batch ${batchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    LogStatus(`Successfully fetched ${allData.length.toLocaleString()} total records for export`);
    return allData;
  }


  private async exportToCSV(data: any[], options: DataGridExportOptions): Promise<void> {
    try {
      // For large datasets, process in chunks to avoid memory issues
      if (data.length > 50000) {
        await this.exportLargeCSVInChunks(data, options);
      } else {
        await this.exportStandardCSV(data, options);
      }

      LogStatus(`Successfully exported ${data.length.toLocaleString()} rows to CSV`);
    } catch (error) {
      LogError('CSV export failed:', undefined, error);
      throw new Error(`CSV export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async exportStandardCSV(data: any[], options: DataGridExportOptions): Promise<void> {
    // Standard CSV export for smaller datasets
    const csvContent = this.generateCSVContent(data, options.includeHeaders);

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `${options.filename}.csv`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  }

  private async exportLargeCSVInChunks(data: any[], options: DataGridExportOptions): Promise<void> {
    const chunkSize = 10000; // Process 10k rows at a time for CSV
    const totalChunks = Math.ceil(data.length / chunkSize);
    const visibleColumns = this.columns.filter(col => col.type !== 'actions');

    let csvContent = '';

    // Add headers first
    if (options.includeHeaders) {
      const headers = visibleColumns.map(col => this.escapeCSV(col.label));
      csvContent += headers.join(',') + '\n';
    }

    // Process data in chunks
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      // Update progress
      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      this.exportProgressMessage = `Generating CSV: Processing chunk ${chunkIndex + 1} of ${totalChunks} (${progress}%)`;

      // Get chunk data
      const startIndex = chunkIndex * chunkSize;
      const endIndex = Math.min(startIndex + chunkSize, data.length);
      const chunkData = data.slice(startIndex, endIndex);

      // Process chunk
      chunkData.forEach(row => {
        const values = visibleColumns.map(col => {
          const value = this.getCellValue(row, col);
          return this.escapeCSV(value);
        });
        csvContent += values.join(',') + '\n';
      });

      // Add small delay to prevent UI freezing for very large datasets
      if (chunkIndex % 5 === 0) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `${options.filename}.csv`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  }

  private generateCSVContent(data: any[], includeHeaders: boolean = true): string {
    const visibleColumns = this.columns.filter(col => col.type !== 'actions');

    let csv = '';

    // Add headers
    if (includeHeaders) {
      const headers = visibleColumns.map(col => this.escapeCSV(col.label));
      csv += headers.join(',') + '\n';
    }

    // Add data rows
    data.forEach(row => {
      const values = visibleColumns.map(col => {
        const value = this.getCellValue(row, col);
        return this.escapeCSV(value);
      });
      csv += values.join(',') + '\n';
    });

    return csv;
  }

  private escapeCSV(value: any): string {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, quotes, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  private async exportToExcel(data: any[], options: DataGridExportOptions): Promise<void> {
    try {
      const visibleColumns = this.columns.filter(col => col.type !== 'actions');
      
      // Prepare data for Excel
      const excelData = data.map(row => {
        const rowData: any = {};
        visibleColumns.forEach(col => {
          // Use the column label as the key for better Excel headers
          rowData[col.label] = this.getCellValue(row, col);
        });
        return rowData;
      });

      // Create a new workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      
      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Data Export');
      
      // Auto-size columns for better readability
      const maxWidths: number[] = [];
      
      // Calculate max width for each column
      visibleColumns.forEach((col, index) => {
        // Start with header length
        let maxWidth = col.label.length;
        
        // Check data in each row
        data.forEach(row => {
          const value = this.getCellValue(row, col);
          if (value && value.length > maxWidth) {
            maxWidth = value.length;
          }
        });
        
        // Add some padding and cap at reasonable width
        maxWidths[index] = Math.min(maxWidth + 2, 50);
      });
      
      // Apply column widths
      ws['!cols'] = maxWidths.map(width => ({ wch: width }));
      
      // Apply some basic styling to the header row
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        
        // Add basic styling (note: full styling requires pro version)
        ws[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "0F2144" } }, // ACS Primary Blue
          alignment: { horizontal: "center" }
        };
      }
      
      // Generate Excel file
      XLSX.writeFile(wb, `${options.filename}.xlsx`);
      
      LogStatus(`Exported ${data.length} rows to Excel`);
    } catch (error) {
      LogError('Excel export failed:', undefined, error);
      alert('Failed to export data to Excel. Please try again.');
    }
  }

  private async exportToPDF(data: any[], options: DataGridExportOptions): Promise<void> {
    try {
      const visibleColumns = this.columns.filter(col => col.type !== 'actions');

      // For large datasets, process in chunks to avoid memory issues
      const CHUNK_THRESHOLD = 5000;

      if (data.length > CHUNK_THRESHOLD) {
        await this.exportLargePDFInChunks(data, visibleColumns, options);
        return;
      }

      // Standard PDF export for smaller datasets
      await this.generatePDFDocument(data, visibleColumns, options);

    } catch (error) {
      LogError('PDF export failed:', undefined, error);
      throw new Error(`PDF export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async exportLargePDFInChunks(data: any[], visibleColumns: DataGridColumn[], options: DataGridExportOptions): Promise<void> {
    // Reduce chunk size further and limit columns to prevent memory overflow
    const chunkSize = 1000; // Smaller chunks for better memory management
    const totalChunks = Math.ceil(data.length / chunkSize);

    // Limit column data to reasonable lengths
    const MAX_CELL_LENGTH = 200; // Reduced from 500

    // Create new PDF document with compression
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a2',
      compress: true,
      putOnlyUsedFonts: true
    });

    this.setupPDFDocument(doc, options);

    const headers = visibleColumns.map(col => col.label);

    // Process chunks
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      try {
        // Update progress
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        this.exportProgressMessage = `Generating PDF: ${progress}% (${chunkIndex + 1}/${totalChunks})`;

        // Get chunk data
        const startIndex = chunkIndex * chunkSize;
        const endIndex = Math.min(startIndex + chunkSize, data.length);
        const chunkData = data.slice(startIndex, endIndex);

        // Prepare chunk rows with aggressive truncation
        const chunkRows = chunkData.map(row =>
          visibleColumns.map(col => {
            const value = this.getCellValue(row, col);
            if (value === null || value === undefined) return '';

            const stringValue = String(value);
            // Truncate to prevent memory issues
            if (stringValue.length > MAX_CELL_LENGTH) {
              return stringValue.substring(0, MAX_CELL_LENGTH - 3) + '...';
            }
            return stringValue;
          })
        );

        // Add table chunk - use simpler styling for large datasets
        // Use dynamic sizing based on column count
        const dynamicSizes = this.calculateDynamicFontSizes(visibleColumns);
        const columnCount = visibleColumns.length;
        const chunkPadding = columnCount > 15 ? 0.5 : 1;

        autoTable(doc, {
          head: [headers], // Include headers in every chunk so they appear on all pages
          body: chunkRows,
          startY: chunkIndex === 0 ? 28 : undefined,
          theme: 'striped',
          tableWidth: 'auto',
          margin: { left: 10, right: 10 },
          styles: {
            fontSize: dynamicSizes.dataFontSize,
            cellPadding: chunkPadding,
            overflow: 'linebreak',
            cellWidth: 'wrap',
            halign: 'left'
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontSize: dynamicSizes.headerFontSize,
            fontStyle: 'bold',
            cellPadding: chunkPadding
          },
          columnStyles: this.getPDFColumnStyles(visibleColumns),
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          didDrawPage: (pageData) => {
            // Simpler page elements for performance
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(8);
            doc.text(
              `Page ${pageData.pageNumber}`,
              doc.internal.pageSize.width / 2,
              pageHeight - 10,
              { align: 'center' }
            );
          },
          showHead: 'everyPage',
          pageBreak: 'auto',
          rowPageBreak: 'avoid'
        });

        // Add delay for garbage collection
        if (chunkIndex % 5 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

      } catch (chunkError) {
        LogError(`Error processing chunk ${chunkIndex + 1}:`, undefined, chunkError);
        throw new Error(`Failed at chunk ${chunkIndex + 1} of ${totalChunks}: ${chunkError instanceof Error ? chunkError.message : 'Unknown error'}`);
      }
    }

    // Add simple summary
    doc.setFontSize(10);
    doc.text(`Total Records: ${data.length.toLocaleString()}`, 14, doc.internal.pageSize.height - 15);

    // Save the PDF
    try {
      doc.save(`${options.filename}.pdf`);
      LogStatus(`Successfully exported ${data.length.toLocaleString()} rows to PDF in ${totalChunks} chunks`);
    } catch (saveError) {
      LogError('PDF save failed:', undefined, saveError);
      throw new Error(`Failed to save PDF: ${saveError instanceof Error ? saveError.message : 'Document too large'}`);
    }
  }

  private async generatePDFDocument(data: any[], visibleColumns: DataGridColumn[], options: DataGridExportOptions): Promise<void> {
    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a2',
      compress: true // Enable compression
    });

    this.setupPDFDocument(doc, options);

    // Prepare table data with truncation for safety
    const headers = visibleColumns.map(col => col.label);
    const rows = data.map(row =>
      visibleColumns.map(col => {
        const value = this.getCellValue(row, col);
        const stringValue = value ? String(value) : '';
        // Truncate long values to prevent memory issues
        return stringValue.length > 500 ? stringValue.substring(0, 497) + '...' : stringValue;
      })
    );

    // Add table using autoTable
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 28,
      theme: 'grid',
      tableWidth: 'auto',
      margin: { left: 10, right: 10 },
      styles: {
        ...this.getPDFStyles(visibleColumns),
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: this.getPDFHeaderStyles(visibleColumns),
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: this.getPDFColumnStyles(visibleColumns),
      didDrawPage: (pageData) => {
        this.addPDFPageElements(doc, pageData);
      },
      // Improve word wrapping
      showHead: 'everyPage',
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      tableLineWidth: 0.1
    });

    this.addPDFSummary(doc, data.length, options);

    // Save the PDF
    doc.save(`${options.filename}.pdf`);
    LogStatus(`Exported ${data.length.toLocaleString()} rows to PDF`);
  }

  private setupPDFDocument(doc: jsPDF, options: DataGridExportOptions): void {
    // Set document properties
    doc.setProperties({
      title: options.filename || 'Data Export',
      subject: 'ACS Chapter Portal Data Export',
      author: 'ACS Chapter Portal',
      keywords: 'data, export, pdf',
      creator: 'ACS Chapter Portal'
    });

    // Add title
    const title = this.exportFilenamePrefix.replace(/-/g, ' ').toUpperCase();
    doc.setFontSize(12);
    doc.setTextColor(15, 33, 68); // ACS Primary Blue
    doc.text(title, 14, 15);

    // Add export date
    doc.setFontSize(8);
    doc.setTextColor(85, 85, 85);
    doc.text(`Exported on: ${new Date().toLocaleDateString()}`, 14, 22);
  }

  private getPDFStyles(visibleColumns: DataGridColumn[]): any {
    const dynamicSizes = this.calculateDynamicFontSizes(visibleColumns);
    const columnCount = visibleColumns.length;

    // Reduce padding for tables with many columns to save space
    const padding = columnCount > 15
      ? { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 }
      : { top: 1, right: 1, bottom: 1, left: 1 };

    // Log sizing decisions for debugging
    LogStatus(`PDF Export: ${columnCount} columns, font size ${dynamicSizes.dataFontSize}, padding ${padding.top}mm`);

    return {
      fontSize: dynamicSizes.dataFontSize,
      cellPadding: padding,
      overflow: 'linebreak',
      halign: 'left',
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      minCellHeight: dynamicSizes.minCellHeight,
      cellWidth: 'wrap'
    };
  }

  private getPDFHeaderStyles(visibleColumns: DataGridColumn[]): any {
    const dynamicSizes = this.calculateDynamicFontSizes(visibleColumns);
    const columnCount = visibleColumns.length;

    // Reduce padding for tables with many columns to save space
    const headerPadding = columnCount > 15
      ? { top: 1, right: 0.5, bottom: 1, left: 0.5 }
      : { top: 2, right: 1, bottom: 2, left: 1 };

    return {
      fillColor: [15, 33, 68],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: dynamicSizes.headerFontSize,
      cellPadding: headerPadding,
      minCellHeight: dynamicSizes.headerMinHeight,
      overflow: 'linebreak',
      cellWidth: 'wrap'
    };
  }

  /**
   * Calculate dynamic font sizes based on number of columns to optimize space usage
   */
  private calculateDynamicFontSizes(visibleColumns: DataGridColumn[]): {
    dataFontSize: number;
    headerFontSize: number;
    minCellHeight: number;
    headerMinHeight: number;
  } {
    try {
      const columnCount = visibleColumns.length;

      // Fallback to safe defaults if no columns
      if (!columnCount || columnCount <= 0) {
        return this.getDefaultPDFSizes();
      }

      // More aggressive font sizing based on column count to fit content
      let dataFontSize: number;
      let headerFontSize: number;

      if (columnCount <= 5) {
        // Few columns: larger fonts for readability
        dataFontSize = 7;
        headerFontSize = 8;
      } else if (columnCount <= 10) {
        // Medium number of columns: balanced approach
        dataFontSize = 6;
        headerFontSize = 7;
      } else if (columnCount <= 15) {
        // Many columns: smaller fonts to fit
        dataFontSize = 5;
        headerFontSize = 6;
      } else if (columnCount <= 20) {
        // Very many columns: more aggressive size reduction
        dataFontSize = 4.5;
        headerFontSize = 5.5;
      } else {
        // Extremely wide tables: minimum readable size
        dataFontSize = 4;
        headerFontSize = 5;
      }

      // Calculate proportional cell heights based on font size
      const minCellHeight = Math.round(dataFontSize * 1.8);
      const headerMinHeight = Math.round(headerFontSize * 2);

      // Safety bounds to ensure readability (lowered minimum for wide tables)
      dataFontSize = Math.max(4, Math.min(8, dataFontSize));
      headerFontSize = Math.max(5, Math.min(9, headerFontSize));

      return {
        dataFontSize: Math.round(dataFontSize * 10) / 10, // Round to 1 decimal place
        headerFontSize: Math.round(headerFontSize * 10) / 10,
        minCellHeight: Math.max(5, minCellHeight),
        headerMinHeight: Math.max(7, headerMinHeight)
      };
    } catch (error) {
      LogError('Error calculating dynamic font sizes, using defaults:', undefined, error);
      return this.getDefaultPDFSizes();
    }
  }

  /**
   * Fallback default PDF sizes for safety
   */
  private getDefaultPDFSizes(): {
    dataFontSize: number;
    headerFontSize: number;
    minCellHeight: number;
    headerMinHeight: number;
  } {
    return {
      dataFontSize: 6,
      headerFontSize: 7,
      minCellHeight: 8,
      headerMinHeight: 10
    };
  }

  private addPDFPageElements(doc: jsPDF, pageData: any): void {
    // Add page numbers
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(
      `Page ${pageData.pageNumber} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - 30,
      doc.internal.pageSize.getHeight() - 10
    );

    // Add footer
    doc.setTextColor(85, 85, 85);
    doc.text(
      'ACS Chapter Portal - Confidential',
      14,
      doc.internal.pageSize.getHeight() - 10
    );
  }

  private addPDFSummary(doc: jsPDF, recordCount: number, options: DataGridExportOptions): void {
    const finalY = (doc as any).lastAutoTable?.finalY || 30;
    if (finalY < doc.internal.pageSize.getHeight() - 40) {
      doc.setFontSize(10);
      doc.setTextColor(85, 85, 85);
      doc.text(`Total Records: ${recordCount.toLocaleString()}`, 14, finalY + 10);

      if (options.selectedOnly && this.selectedItems.size > 0) {
        doc.text('Note: This export contains only selected records.', 14, finalY + 16);
      }
    }
  }

  private getPDFColumnStyles(columns: DataGridColumn[]): any {
    const styles: any = {};

    const columnCount = columns.length;
    // Calculate available width: A2 landscape ~594mm - margins ~30mm = 564mm usable
    // Reduce slightly more for wide tables to ensure content fits (conservative approach)
    const availableWidth = columnCount > 20 ? 535 : (columnCount > 15 ? 545 : 564);
    const averageWidth = availableWidth / columnCount;

    // Get dynamic font size info
    const dynamicSizes = this.calculateDynamicFontSizes(columns);

    // First pass: calculate initial column widths
    const initialWidths: number[] = [];
    columns.forEach((col, index) => {
      const width = this.calculatePDFColumnWidth(col, averageWidth, columnCount, dynamicSizes.dataFontSize);
      initialWidths[index] = width;
    });

    // Check if total width exceeds available space and adjust if needed
    const adjustedWidths = this.ensureColumnsCanFit(initialWidths, availableWidth, columnCount);

    // Apply adjusted widths to styles
    columns.forEach((col, index) => {
      styles[index] = {
        halign: col.align || (col.type === 'number' ? 'right' : 'left'),
        cellWidth: adjustedWidths[index]
      };
    });

    return styles;
  }

  private calculatePDFColumnWidth(column: DataGridColumn, averageWidth: number, columnCount: number, fontSize: number): number {
    const key = column.key.toLowerCase();
    const headerLength = column.label.length;

    // Aggressive space saving for many columns - ensure all columns fit!
    let minWidthForHeader: number;
    let maxColumnWidth: number;

    if (columnCount > 20) {
      // Extremely wide tables: maximum compression
      minWidthForHeader = Math.max(6, headerLength * 0.7);
      maxColumnWidth = 20; // Ultra tight limit
    } else if (columnCount > 15) {
      // Very many columns: ultra-compact mode
      minWidthForHeader = Math.max(7, headerLength * 0.75);
      maxColumnWidth = 22; // Very tight limit
    } else if (columnCount > 10) {
      // Many columns: compact mode
      minWidthForHeader = Math.max(10, headerLength * 1.0);
      maxColumnWidth = 35;
    } else if (columnCount > 5) {
      // Medium columns: balanced mode
      minWidthForHeader = Math.max(12, headerLength * 1.2);
      maxColumnWidth = 45;
    } else {
      // Few columns: generous mode
      minWidthForHeader = Math.max(15, headerLength * 1.5);
      maxColumnWidth = 60;
    }

    // Get content-based width multiplier (reduced for many columns)
    let contentMultiplier = 1.0;

    if (columnCount > 10) {
      // Aggressive compression for many columns
      if (key.includes('email') || key.includes('address')) {
        contentMultiplier = 1.2; // Reduced from 1.5
      } else if (key.includes('name') && !key.includes('chapter')) {
        contentMultiplier = 1.1; // Reduced from 1.3
      } else if (key.includes('chaptername') || key.includes('description')) {
        contentMultiplier = 1.1; // Reduced from 1.4
      } else if (key.includes('phone')) {
        contentMultiplier = 0.9; // Reduced from 1.1
      } else if (key.includes('id') || key.includes('memberid') || key.includes('member_id')) {
        contentMultiplier = 0.6; // Reduced from 0.8
      } else if (key.includes('state') || key.includes('zip')) {
        contentMultiplier = 0.5; // Reduced from 0.7
      } else if (column.type === 'boolean') {
        contentMultiplier = 0.4; // Reduced from 0.6
      } else if (column.type === 'number' || column.type === 'date') {
        contentMultiplier = 0.7; // Reduced from 0.9
      } else {
        contentMultiplier = 0.8; // Default reduced
      }
    } else {
      // Normal spacing for fewer columns
      if (key.includes('email') || key.includes('address')) {
        contentMultiplier = 1.5;
      } else if (key.includes('name') && !key.includes('chapter')) {
        contentMultiplier = 1.3;
      } else if (key.includes('chaptername') || key.includes('description')) {
        contentMultiplier = 1.4;
      } else if (key.includes('phone')) {
        contentMultiplier = 1.1;
      } else if (key.includes('id') || key.includes('memberid') || key.includes('member_id')) {
        contentMultiplier = 0.8;
      } else if (key.includes('state') || key.includes('zip')) {
        contentMultiplier = 0.7;
      } else if (column.type === 'boolean') {
        contentMultiplier = 0.6;
      } else if (column.type === 'number' || column.type === 'date') {
        contentMultiplier = 0.9;
      }
    }

    // Calculate final width
    const baseWidth = averageWidth * contentMultiplier;
    const finalWidth = Math.max(minWidthForHeader, baseWidth);

    // Apply bounds based on column count
    return Math.max(8, Math.min(maxColumnWidth, finalWidth));
  }

  /**
   * Ensure all columns fit within available width and utilize space efficiently
   */
  private ensureColumnsCanFit(columnWidths: number[], availableWidth: number, columnCount: number): number[] {
    // Calculate total width needed
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const spaceUtilization = (totalWidth / availableWidth) * 100;

    // Case 1: Columns exceed available space - need compression
    if (totalWidth > availableWidth) {
      // We need to compress - calculate compression ratio
      const compressionRatio = availableWidth / totalWidth;
      LogStatus(`PDF columns need compression: ${totalWidth}mm > ${availableWidth}mm, applying ${Math.round(compressionRatio * 100)}% compression`);

      // Apply compression while maintaining minimum widths
      // More aggressive minimums for wide tables
      const minAbsoluteWidth = columnCount > 20 ? 4.5 : (columnCount > 15 ? 5 : 8);
      const adjustedWidths = columnWidths.map(width => {
        const compressedWidth = width * compressionRatio;
        return Math.max(minAbsoluteWidth, compressedWidth);
      });

      // Check if we still exceed after minimum width constraints
      const finalTotalWidth = adjustedWidths.reduce((sum, width) => sum + width, 0);

      if (finalTotalWidth > availableWidth) {
        // Still too wide - force equal distribution as last resort
        LogStatus(`PDF columns still too wide after compression, forcing equal distribution`);
        const equalWidth = Math.max(minAbsoluteWidth, Math.floor(availableWidth / columnCount));
        return new Array(columnCount).fill(equalWidth);
      }

      LogStatus(`PDF columns compressed successfully: ${Math.round(finalTotalWidth)}mm total width`);
      return adjustedWidths;
    }

    // Case 2: Good space utilization (80-100%) - use as-is
    if (spaceUtilization >= 80) {
      LogStatus(`PDF columns have good space utilization: ${totalWidth}mm used (${Math.round(spaceUtilization)}%) of ${availableWidth}mm available`);
      return columnWidths;
    }

    // Case 3: Poor space utilization (< 80%) - expand columns to better use space
    LogStatus(`PDF columns underutilizing space: ${totalWidth}mm used (${Math.round(spaceUtilization)}%) of ${availableWidth}mm available, expanding columns`);

    // Calculate target utilization (aim for 85-90% to leave some breathing room)
    const targetUtilization = columnCount <= 5 ? 0.90 : 0.85;
    const targetTotalWidth = availableWidth * targetUtilization;
    const expansionRatio = targetTotalWidth / totalWidth;

    // Apply expansion with reasonable maximum column widths
    const maxColumnWidth = columnCount <= 3 ? 80 : columnCount <= 5 ? 70 : 60;

    const expandedWidths = columnWidths.map(width => {
      const expandedWidth = width * expansionRatio;
      return Math.min(maxColumnWidth, expandedWidth);
    });

    const finalTotalWidth = expandedWidths.reduce((sum, width) => sum + width, 0);
    LogStatus(`PDF columns expanded successfully: ${Math.round(finalTotalWidth)}mm total width (${Math.round((finalTotalWidth/availableWidth)*100)}% utilization)`);

    return expandedWidths;
  }


  isStatusColumn(column: DataGridColumn): boolean {
    // Check if column type is explicitly set to 'badge'
    if (column.type === 'badge') {
      return true;
    }

    // Check common status column keys
    const key = column.key.toLowerCase();
    return ['status', 'statuslabel', 'isactive', 'active'].includes(key);
  }

  isPersonCard(item: any): boolean {
    // Check if the item has firstName and lastName properties (member/officer data)
    return item && (item.firstName || item.lastName || (item.member?.firstName));
  }

  getPersonName(item: any): string {
    // Handle direct member data
    if (item.firstName || item.lastName) {
      return `${item.firstName || ''} ${item.lastName || ''}`.trim();
    }
    // Handle officer data (has nested member object)
    if (item.member) {
      return `${item.member.firstName || ''} ${item.member.lastName || ''}`.trim();
    }
    return 'Unknown';
  }

  getPersonCardColumns(): DataGridColumn[] {
    // Return columns that should be shown in person cards (excluding name-related columns)
    return this.columns.filter(col => 
      col.key !== 'firstName' && 
      col.key !== 'lastName' && 
      col.key !== 'fullName' && 
      col.key !== 'member' &&
      col.key !== 'name'
    ).slice(0, 4); // Show up to 4 additional details
  }

  getDisplayColumns(): DataGridColumn[] {
    // Return columns for enhanced card display (excluding actions)
    return this.columns.filter(col => col.type !== 'actions').slice(0, 5);
  }

  getCardIcon(column: DataGridColumn): string {
    const iconMap: { [key: string]: string } = {
      'Email': '📧',
      'Phone': '📞',
      'Address1': '📍',
      'Address2': '📍',
      'Address3': '📍',
      'City': '🏙️',
      'State': '🗺️',
      'ZipCode': '📮',
      'Status': '🎫',
      'MemberClass': '🎫',
      'MemberStatus': '●',
      'userAction': '●',
      'IsActive': '●',
      'ACSChapterDuesOptInDate': '📅',
      'AssociateFellowDate': '📅',
      'ResidentDate': '📅',
      'MedicalStudentDate': '📅',
      'AffiliateDate': '📅',
      'ChapterStartDate': '📅',
      'UploadDate': '📅',
      'EffectiveDate': '📅',
      'StartDate': '📅',
      'EndDate': '📅',
      '__mj_CreatedAt': '📅',
      '__mj_UpdatedAt': '📅',
      'PublishDate': '📅',
      'Role': '👔',
      'Name': '🏥',
      'ChapterName': '🏥',
      'Chapter': '🏥',
      'CommitteeName': '🏥',
      'MemberID': '🆔',
      // Financial/Dues data icons
      'amount': '💵',
      'invoicedAmount': '💵',
      'totalAmount': '💵',
      'dueAmount': '💵',
      'paidAmount': '💵',
      'paymentDate': '📅',
      'dueDate': '📅',
      'invoiceDate': '📅',
      'transactionDate': '📅',
      'transactionId': '🧾',
      'invoiceId': '🧾',
      'receiptId': '🧾',
      'paymentMethod': '💳',
      'paymentType': '💳',
      'refundAmount': '💰',
      'refundDate': '📅',
      'auditDate': '🔍',
      'createdDate': '📅',
      'modifiedDate': '📅',
      'Description': '📝',
      'notes': '📝',
      'reason': '📝',
      'category': '🏷️',
      'type': '🏷️',
      'MailName': '👤',
      'FirstName': '👤',
      'LastName': '👤',
      'YFAMember': '👤'
    };
    return iconMap[column.key] || '📄';
  }

  getCardIconClass(column: DataGridColumn): string {
    const classMap: { [key: string]: string } = {
      'email': 'info-card__icon--email',
      'phone': 'info-card__icon--phone',
      'address': 'info-card__icon--location',
      'city': 'info-card__icon--location',
      'state': 'info-card__icon--location',
      'zipCode': 'info-card__icon--location',
      'membershipType': 'info-card__icon--membership',
      'membershipTypeName': 'info-card__icon--membership'
    };
    return classMap[column.key] || '';
  }

  getCardDetailClass(column: DataGridColumn): string {
    const classMap: { [key: string]: string } = {
      'email': 'info-card__email',
      'phone': 'info-card__phone',
      'status': 'info-card__status',
      'statusLabel': 'info-card__status'
    };
    return classMap[column.key] || '';
  }

  /**
   * Format date string to MM/DD/YYYY without timezone conversion
   * Uses dayjs UTC mode to prevent timezone shifts for all users
   * @param value - Date value (Date object or SQL string format)
   * @returns Formatted date string in MM/DD/YYYY format
   */
  private formatDateValue(value: any): string {
    if (!value) return '';

    try {
      // Use dayjs.utc() to parse the date in UTC mode without timezone conversion
      // This works for both Date objects and string formats
      const date = dayjs.utc(value);

      if (!date.isValid()) {
        return '';
      }

      return date.format('MM/DD/YYYY');
    } catch (error) {
      LogError(`Error formatting date. Value: ${value}.`, null, error);
      return '';
    }
  }

  getCellValue(item: any, column: DataGridColumn): string {
    const value = item[column.key];

    if (column.format) {
      return column.format(value, item);
    }

    if (value === null || value === undefined) {
      return '';
    }

    switch (column.type) {
      case 'date':
        return this.formatDateValue(value);
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'number':
        return Number(value).toString();
      default:
        return String(value);
    }
  }

  shouldWrapColumn(column: DataGridColumn): boolean {
    // Check column-specific setting first
    if (column.wrapText !== undefined) {
      return column.wrapText;
    }
    // Fall back to grid-level setting (default is wrap, truncate only if truncateText is true)
    return !this.truncateText;
  }

  shouldClampColumn(column: DataGridColumn): boolean {
    // Don't clamp if truncating (single line ellipsis)
    if (!this.shouldWrapColumn(column)) {
      return false;
    }
    // Check if line clamping is enabled
    return this.clampLines;
  }

  getMaxLines(column: DataGridColumn): number {
    // Column-specific max lines
    if (column.maxLines !== undefined) {
      return column.maxLines;
    }
    // Grid-level max lines
    return this.maxLines;
  }

  shouldShowTooltip(column: DataGridColumn): boolean {
    // Show tooltip if:
    // 1. Text is truncated (single line)
    // 2. Text is clamped (multi-line)
    // 3. Tooltips are enabled
    return this.showTooltips && (!this.shouldWrapColumn(column) || this.shouldClampColumn(column));
  }

  checkTextOverflow(event: MouseEvent, item: any, column: DataGridColumn): void {
    if (!this.showTooltips) return;

    const element = event.target as HTMLElement;

    // Check if text is overflowing
    const isOverflowing = element.scrollHeight > element.clientHeight ||
                         element.scrollWidth > element.clientWidth;

    // Only set title if text is actually overflowing
    if (isOverflowing) {
      element.title = this.getCellValue(item, column);
    } else {
      element.title = '';
    }
  }

  getCellClass(column: DataGridColumn, item: any): string {
    if (!column.cellClass) {
      return '';
    }

    if (typeof column.cellClass === 'string') {
      return column.cellClass;
    }

    if (typeof column.cellClass === 'function') {
      return column.cellClass(item);
    }

    return '';
  }

  getSortIcon(column: DataGridColumn): string {
    if (!column.sortable || !this.currentSort || this.currentSort.column !== column.key) {
      return '↕️';
    }
    return this.currentSort.direction === 'asc' ? '↑' : '↓';
  }

  // Column resizing
  startColumnResize(column: DataGridColumn, event: MouseEvent): void {
    if (!this.resizableColumns) return;

    event.preventDefault();
    event.stopPropagation();

    this.resizing = {
      column: column.key,
      startX: event.clientX,
      startWidth: 150 // Default width, should be calculated from actual width
    };

    document.addEventListener('mousemove', this.onColumnResize.bind(this));
    document.addEventListener('mouseup', this.onColumnResizeEnd.bind(this));
  }

  private onColumnResize(event: MouseEvent): void {
    if (!this.resizing) return;

    const deltaX = event.clientX - this.resizing.startX;
    const newWidth = Math.max(50, this.resizing.startWidth + deltaX);
    
    const column = this.columns.find(col => col.key === this.resizing!.column);
    if (column) {
      column.width = `${newWidth}px`;
    }
  }

  private onColumnResizeEnd(): void {
    this.resizing = null;
    document.removeEventListener('mousemove', this.onColumnResize);
    document.removeEventListener('mouseup', this.onColumnResizeEnd);
  }

  get containerClasses(): string {
    const classes = ['acs-data-grid'];
    if (this.loading) classes.push('acs-data-grid--loading');
    if (this.viewMode === 'card') classes.push('acs-data-grid--card-view');
    if (this.stickyHeader) classes.push('acs-data-grid--sticky-header');
    if (this.truncateText) classes.push('acs-data-grid--truncate');
    if (this.clampLines && !this.truncateText) classes.push('acs-data-grid--clamp');
    return classes.join(' ');
  }

  get totalHeight(): number {
    if (this.serverSide) {
      // In server-side mode, we need to calculate total height based on total count
      return this.totalCount * this.itemHeight;
    }
    return this.filteredData.length * this.itemHeight;
  }

  get offsetY(): number {
    if (this.serverSide) {
      // In server-side mode, don't apply transform offset for regular pagination
      // Only apply offset for true infinite scroll mode
      return this.infiniteScroll && this.virtualScrolling ? this.visibleStartIndex * this.itemHeight : 0;
    }
    return this.virtualScrolling ? this.visibleStartIndex * this.itemHeight : 0;
  }


  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  // ========== Server-Side Data Grid Methods ==========

  /**
   * Initialize server-side mode
   * ✅ Sets up server-side data loading
   */
  private initializeServerSide(): void {
    if (!this.autoLoad) {
      return;
    }

    // If committee filtering is enabled, wait for committee access to load before fetching data
    if (this.enableCommitteeFiltering) {
      this.subscriptions.add(
        this.userCommitteeAccessService.isLoading$.pipe(
          filter(isLoading => !isLoading), // Wait until committee access is not loading
          take(1) // Only take the first emission
        ).subscribe(() => {
          LogStatus('AcsDataGrid: Committee access loaded, now loading data');
          this.loadServerData();
        })
      );
    } else {
      // No committee filtering, load data immediately
      this.loadServerData();
    }
  }

  /**
   * Build complete filter SQL combining all filter sources
   * Used by both data loading and export functionality
   */
  private buildCompleteFilterSQL(): string {
    const filterParts: string[] = [];

    // 1. Add committee access filtering (highest priority for security)
    if (this.enableCommitteeFiltering && this.serverSide) {
      const committeeFilter = this.buildCommitteeAccessFilter();
      if (committeeFilter) {
        filterParts.push(`(${committeeFilter})`);
      }
    }

    // 2. Add server-side filters (second priority)
    const serverFilterSQL = this.buildServerSideFilterSQL();
    if (serverFilterSQL) {
      filterParts.push(serverFilterSQL);
    }

    // 3. Add column filters from the grid UI
    if (this.currentFilters.length > 0) {
      const filterStrings = this.currentFilters.map(filter => {
        // Special handling for Age column with range values
        if (filter.column === 'Age' && typeof filter.value === 'string') {
          return this.buildAgeRangeFilter(filter.value);
        }

        if (filter.column === 'ChapterDuesBalance' && typeof filter.value === 'string') {
          return this.buildChapterDuesBalanceFilter(filter.value);
        }

        switch (filter.operator || 'contains') {
          case 'equals':
            return `${filter.column} = '${filter.value}'`;
          case 'contains':
            return `${filter.column} LIKE '%${filter.value}%'`;
          case 'startsWith':
            return `${filter.column} LIKE '${filter.value}%'`;
          case 'endsWith':
            return `${filter.column} LIKE '%${filter.value}'`;
          case 'greaterThan':
            return `${filter.column} > ${filter.value}`;
          case 'lessThan':
            return `${filter.column} < ${filter.value}`;
          default:
            return `${filter.column} LIKE '%${filter.value}%'`;
        }
      });
      const columnFiltersSQL = filterStrings.join(' AND ');
      if (columnFiltersSQL) {
        filterParts.push(`(${columnFiltersSQL})`);
      }
    }

    // 4. Add search filter if present
    if (this.searchValue && this.searchValue.trim()) {
      const searchColumns = this.columns
        .filter(col => col.type !== 'actions' && col.filterable !== false)
        .map(col => `${col.key} LIKE '%${this.searchValue.trim()}%'`)
        .join(' OR ');

      if (searchColumns) {
        filterParts.push(`(${searchColumns})`);
      }
    }

    // Combine all filters with AND
    return filterParts.join(' AND ');
  }

  /**
   * Build committee access filter based on user's role and committee assignments
   * Chapter Admins see only their committee data, Staff see all data
   */
  private buildCommitteeAccessFilter(): string {
    try {
      // Check if we should apply committee filtering
      if (!this.enableCommitteeFiltering || !this.committeeColumnName) {
        return '';
      }

      // Get the active user for logging
      const activeUser = this.activeUserService.getActiveUser();
      if (!activeUser) {
        LogError('AcsDataGrid: No active user found for committee filtering');
        return '';
      }

      // Check user role using UserAuthService - Staff users have no restrictions
      if (this.userAuthService.isStaff()) {
        LogStatus(`AcsDataGrid: User ${activeUser.Name} has Staff role - no committee filtering applied`);
        return '';
      }

      // Check if user has no access at all
      if (!this.userAuthService.hasAccess()) {
        LogStatus(`AcsDataGrid: User ${activeUser.Name} has no access - returning restrictive filter`);
        return `${this.committeeColumnName} = ''`;
      }

      // For Chapter Admins and other roles with access, apply committee access filtering
      const committeeFilter = this.userCommitteeAccessService.getCommitteeAccessFilter(this.committeeColumnName);

      if (committeeFilter) {
        const userRole = this.userAuthService.getUserRole();
        LogStatus(`AcsDataGrid: Applied committee access filter for ${activeUser.Name} (${userRole}): ${committeeFilter}`);
      } else {
        LogStatus(`AcsDataGrid: No committee access restrictions for ${activeUser.Name}`);
      }

      return committeeFilter;

    } catch (error) {
      LogError('AcsDataGrid: Error building committee access filter', undefined, error);
      // Return restrictive filter on error for security
      return `${this.committeeColumnName} = ''`;
    }
  }

  /**
   * Build SQL WHERE clause from server-side filters
   * Handles both individual filters and filter groups with proper AND/OR logic
   */
  private buildServerSideFilterSQL(): string {
    if (!this.serverSideFilters || this.serverSideFilters.length === 0) {
      return '';
    }

    // Helper function to check if item is a filter group
    const isFilterGroup = (item: ServerSideFilter | ServerSideFilterGroup): item is ServerSideFilterGroup => {
      return 'filters' in item;
    };

    // Sort filters by priority (if specified)
    const sortedFilters = [...this.serverSideFilters].sort((a, b) => {
      const priorityA = isFilterGroup(a) ? 0 : (a.priority || 999);
      const priorityB = isFilterGroup(b) ? 0 : (b.priority || 999);
      return priorityA - priorityB;
    });

    // Process each filter/group
    const filterClauses: string[] = [];

    sortedFilters.forEach((item, index) => {
      if (isFilterGroup(item)) {
        // Process filter group
        const groupClauses = item.filters
          .filter(f => f.sql && f.sql.trim() !== '')
          .map(f => f.sql.trim());

        if (groupClauses.length > 0) {
          const groupOperator = item.groupOperator || 'AND';
          const groupSQL = groupClauses.join(` ${groupOperator} `);
          
          // Wrap in parentheses if specified or if there are multiple clauses
          const finalGroupSQL = (item.wrapInParentheses !== false && groupClauses.length > 1) 
            ? `(${groupSQL})` 
            : groupSQL;
          
          filterClauses.push(finalGroupSQL);
        }
      } else {
        // Process individual filter
        if (item.sql && item.sql.trim() !== '') {
          filterClauses.push(item.sql.trim());
        }
      }
    });

    // Combine filter clauses respecting individual operators
    if (filterClauses.length === 0) {
      return '';
    }
    
    if (filterClauses.length === 1) {
      return filterClauses[0];
    }
    
    // Build the final SQL respecting operator precedence
    let result = filterClauses[0];
    
    for (let i = 1; i < filterClauses.length; i++) {
      // Check if the previous filter specified an operator for the next combination
      const prevItem = sortedFilters[i - 1];
      let operator = 'AND';
      
      if (!isFilterGroup(prevItem) && prevItem.groupOperator) {
        operator = prevItem.groupOperator;
      }
      
      result = `${result} ${operator} ${filterClauses[i]}`;
    }
    
    return result;
  }

  /**
   * Load data from server using RunView directly
   * ✅ Efficient MemberJunction integration
   */
  async loadServerData(): Promise<void> {
    if (!this.entityName) {
      LogError('❌ AcsDataGrid - entityName is required for server-side mode');
      return;
    }

    this.serverLoading = true;
    this.serverErrorMessage = null;

    try {
      const rv = new RunView();
      
      // Build complete filter SQL using centralized method
      const extraFilter = this.buildCompleteFilterSQL();

      // Build OrderBy string
      let orderBy = '';
      if (this.currentSort) {
        orderBy = `${this.currentSort.column} ${this.currentSort.direction.toUpperCase()}`;
      }


      const result = await rv.RunView({
        EntityName: this.entityName,
        ExtraFilter: extraFilter || undefined,
        OrderBy: orderBy || undefined,
        StartRow: this.currentStartRow,
        MaxRows: this.pageSize,
        ResultType: 'entity_object'
      });

      if (result.Success) {
        const currentRowCount = result.Results ? result.Results.length : 0;
        const response: DataGridResponse<any> = {
          data: result.Results || [],
          totalCount: result.TotalRowCount || 0,
          totalPages: Math.ceil((result.TotalRowCount || 0) / this.pageSize),
          page: this.currentPage,
          pageSize: this.pageSize,
          hasNextPage: currentRowCount === this.pageSize && 
                      (this.currentStartRow + currentRowCount) < (result.TotalRowCount || 0),
          hasPreviousPage: this.currentStartRow > 0,
          success: true
        };


        this.serverResponse = response;
        this.lastResponseRowCount = response.data.length;
        
        // Handle data accumulation with StartRow tracking
        const isNewSearch = this.currentStartRow === 0;
        const isSubsequentLoad = this.currentStartRow > 0;
        
        if (isSubsequentLoad) {
          // For any subsequent page load (scroll-triggered), append the data
          this.allLoadedData.push(...response.data);
          this.displayedData = [...this.allLoadedData];
        } else {
          // Replace data for new searches, sorts, or first page
          this.allLoadedData = [...response.data];
          this.displayedData = response.data;
        }
        
        // Update pagination state from response
        this.hasNextPage = response.hasNextPage;
        this.hasPreviousPage = response.hasPreviousPage;
        
        // Note: currentStartRow is now updated in goToNextPage() before loading
        
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.serverLoading = false;

        this.serverDataLoaded.emit(response);

        // Pre-load dropdown filters after successful data load
        if (this.enableDropdownFilters) {
          setTimeout(() => this.preloadDropdownFilters(), 100);
        }
      } else {
        throw new Error(result.ErrorMessage || 'Failed to load data');
      }
    } catch (error) {
      LogError('AcsDataGrid - Server data loading error:', undefined, error);
      this.serverErrorMessage = (error as Error).message || 'Failed to load data';
      this.serverLoading = false;
      this.serverError.emit(error);
    }
  }

  /**
   * Handle server-side sorting
   * ✅ Triggers server reload with new sort parameters
   */
  onServerSort(column: DataGridColumn): void {
    if (!column.sortable || !this.sortable) return;

    const newSort: DataGridSort = {
      column: column.key,
      direction: this.currentSort?.column === column.key && this.currentSort?.direction === 'asc' ? 'desc' : 'asc'
    };

    this.currentSort = newSort;
    this.currentPage = 1; // Reset to first page when sorting
    this.currentStartRow = 0; // MJ recommended: Reset StartRow for new sort
    this.allLoadedData = []; // Clear accumulated data for new sort
    this.displayedData = [];
    this.sort.emit(newSort);
    this.loadServerData();
  }

  /**
   * Handle server-side filtering  
   * ✅ Triggers server reload with new filter parameters
   */
  onServerFilter(column: string, value: any): void {
    // Set loading state while filter is being applied
    this.serverLoading = true;

    const existingFilterIndex = this.currentFilters.findIndex(f => f.column === column);

    if (value === null || value === undefined || value === '') {
      if (existingFilterIndex >= 0) {
        this.currentFilters.splice(existingFilterIndex, 1);
      }
    } else {
      // Check if this is a dropdown filter column
      const columnDef = this.columns.find(col => col.key === column);
      const isDropdownFilter = columnDef?.dropdownFilter === true;

      // Use 'equals' operator for dropdown filters, 'contains' for text filters
      const filter: DataGridFilter = {
        column,
        value,
        operator: isDropdownFilter ? 'equals' : 'contains'
      };

      if (existingFilterIndex >= 0) {
        this.currentFilters[existingFilterIndex] = filter;
      } else {
        this.currentFilters.push(filter);
      }
    }

    this.currentPage = 1; // Reset to first page when filtering
    this.currentStartRow = 0; // MJ recommended: Reset StartRow for new filter
    this.allLoadedData = []; // Clear accumulated data for new filter
    this.displayedData = [];
    this.filter.emit(this.currentFilters);
    this.loadServerData();
  }

  /**
   * Handle server-side search
   * ✅ Updates search term and reloads data
   */
  onServerSearch(value: string): void {
    this.searchValue = value;
    this.currentPage = 1; // Reset to first page when searching
    this.currentStartRow = 0; // MJ recommended: Reset StartRow for new search
    this.allLoadedData = []; // Clear accumulated data for new search
    this.displayedData = [];
    this.loadServerData();
  }

  /**
   * Handle page change
   * ✅ Loads data for new page
   */
  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    
    this.currentPage = page;
    this.pageChange.emit(page);
    
    if (this.serverSide) {
      // Update StartRow based on page (for regular pagination, not infinite scroll)
      if (!this.infiniteScroll) {
        this.currentStartRow = (page - 1) * this.pageSize;
      }
      this.loadServerData();
    }
  }

  /**
   * Handle page size change
   * ✅ Updates page size and reloads data
   */
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.currentStartRow = 0; // Reset StartRow when changing page size
    this.allLoadedData = []; // Clear accumulated data
    this.displayedData = [];
    this.pageSizeChange.emit(pageSize);
    
    if (this.serverSide) {
      this.loadServerData();
    }
  }

  /**
   * Go to next page
   * ✅ Server-side pagination support
   */
  goToNextPage(): void {
    if (this.infiniteScroll) {
      // For infinite scroll, load more data and update startRow
      if (this.hasNextPage && !this.serverLoading) {
        // Update currentStartRow for next load before calling loadServerData
        this.currentStartRow += this.pageSize;
        this.loadServerData();
      }
    } else if (this.hasNextPage) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  /**
   * Go to previous page
   * ✅ Server-side pagination support
   */
  goToPreviousPage(): void {
    if (this.hasPreviousPage) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  /**
   * Go to first page
   * ✅ Server-side pagination support
   */
  goToFirstPage(): void {
    if (this.currentPage > 1) {
      this.onPageChange(1);
    }
  }

  /**
   * Go to last page
   * ✅ Server-side pagination support
   */
  goToLastPage(): void {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.totalPages);
    }
  }

  /**
   * Clear all filters and search
   * ✅ Server-side filter clearing
   */
  clearServerFilters(): void {
    this.currentFilters = [];
    this.filterValues = {};
    this.searchValue = '';
    this.currentPage = 1;
    this.currentStartRow = 0; // MJ recommended: Reset StartRow when clearing filters
    this.allLoadedData = []; // Clear accumulated data for fresh start
    this.displayedData = [];
    this.filter.emit(this.currentFilters);
    
    if (this.serverSide) {
      this.loadServerData();
    }
  }

  // ========== Hybrid Mode Support ==========
  // Methods updated to handle both client and server modes

  /**
   * Public method to refresh data - can be called from parent components
   */
  refreshData(): void {
    if (this.serverSide) {
      this.refreshServerData();
    } else {
      // Client-side refresh - reprocess data and emit event
      try {
        this.processData();
        
        // Emit data loaded event for client-side refresh
        const response = {
          data: this.displayedData,
          totalCount: this.filteredData.length,
          totalPages: 1,
          page: 1,
          pageSize: this.filteredData.length,
          hasNextPage: false,
          hasPreviousPage: false,
          success: true
        };
        
        this.serverDataLoaded.emit(response);
      } catch (error) {
        this.serverError.emit(error);
      }
    }
  }

  /**
   * Public method to refresh server data specifically
   * ✅ Reloads data from server with reset pagination
   */
  refreshServerData(): void {
    if (this.serverSide) {
      // Reset to first page and clear accumulated data
      this.currentPage = 1;
      this.currentStartRow = 0;
      this.allLoadedData = [];
      this.displayedData = [];
      this.loadServerData();
    }
  }

  // ========== Computed Properties for Templates ==========

  get isServerSideMode(): boolean {
    return this.serverSide;
  }

  get isClientSideMode(): boolean {
    return !this.serverSide;
  }

  get effectiveLoading(): boolean {
    return this.serverSide ? this.serverLoading : this.loading;
  }

  get effectiveError(): string | null {
    return this.serverSide ? this.serverErrorMessage : null;
  }

  get effectiveTotalCount(): number {
    return this.serverSide ? this.totalCount : this.filteredData.length;
  }

  get showPaginationControls(): boolean {
    return this.serverSide && this.showPagination && this.totalPages > 1;
  }

  get paginationInfo(): string {
    if (!this.serverSide) return '';
    
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalCount);
    return `${start}-${end} of ${this.totalCount}`;
  }

  get fixedContainerHeight(): number {
    // Return fixed height for template, never allow it to be 0
    return Math.max(this.containerHeight, 400);
  }

  // ============================================================
  // DROPDOWN FILTER METHODS
  // ============================================================

  /**
   * Task 2.1: Check if column has custom formatting
   */
  /**
   * Determines if a column has custom formatting that affects dropdown display.
   *
   * @param column - The column to check for custom formatting
   * @returns True if the column has custom formatting (custom format function or built-in types)
   *
   * @example
   * ```typescript
   * const column = { key: 'status', format: (value) => value.toUpperCase() };
   * const hasFormatting = this.hasCustomFormatting(column); // true
   * ```
   */
  hasCustomFormatting(column: DataGridColumn): boolean {
    // Check for custom format function
    if (column.format) {
      return true;
    }

    // Check for built-in type formatting
    const formattedTypes = ['date', 'boolean', 'number'];
    return formattedTypes.includes(column.type || '');
  }

  /**
   * Task 2.3: Format value for display (without full item context)
   */
  /**
   * Formats a value for display in dropdown options based on column configuration.
   *
   * @param value - The raw value to format
   * @param column - The column configuration containing formatting rules
   * @returns Formatted string for display
   *
   * @example
   * ```typescript
   * // Custom formatter
   * const column = { format: (val) => val === 1 ? 'High' : 'Low' };
   * const formatted = this.formatValueForDisplay(1, column); // 'High'
   *
   * // Date formatting
   * const dateColumn = { type: 'date' };
   * const formatted = this.formatValueForDisplay('2023-01-01', dateColumn); // '1/1/2023'
   *
   * // Boolean formatting
   * const boolColumn = { type: 'boolean' };
   * const formatted = this.formatValueForDisplay(true, boolColumn); // 'Yes'
   * ```
   */
  formatValueForDisplay(value: any, column: DataGridColumn): string {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return '';
    }

    // Use simplified formatter if provided
    if (column.formatForFilter) {
      try {
        return column.formatForFilter(value);
      } catch (error) {
        console.warn(`Error in formatForFilter for column ${column.key}:`, error);
        // Fall through to default formatting
      }
    }

    // Use custom formatter (pass null for item context)
    if (column.format) {
      try {
        return column.format(value, null);
      } catch (error) {
        console.warn(`Error in custom format for column ${column.key}:`, error);
        // Fall through to built-in formatting
      }
    }

    // Handle built-in type formatting (same logic as getCellValue)
    switch (column.type) {
      case 'date':
        return this.formatDateValue(value);
      case 'boolean':
        // Handle boolean values stored as 0/1 or true/false
        const boolValue = value === true || value === 1 || value === '1' || value === 'true';
        return boolValue ? 'Yes' : 'No';
      case 'number':
        return Number(value).toString();
      default:
        return String(value);
    }
  }

  /**
   * Task 2.2: Get distinct raw values for a column
   */
  async getDistinctValuesRaw(column: DataGridColumn): Promise<any[]> {
    if (!this.entityName || !this.serverSide) {
      // Client-side: use current data
      // Return empty array if data hasn't loaded yet
      if (!this.data || this.data.length === 0) {
        return [];
      }
      const values = this.data.map(item => item[column.key]);
      return Array.from(new Set(values)).filter(v => v != null).sort();
    }

    try {

      const md = new Metadata();
      const entityInfo = md.EntityByName(this.entityName);
      const rq = new RunQuery();
      const whereCondition = this.buildCurrentFiltersForDistinct(column.key);
      const result = await rq.RunQuery({
        QueryName: QueryNames.DistinctColumnValues,
        Parameters: {
          ColumnName: column.key,
          SchemaName: entityInfo.SchemaName,
          TableName: entityInfo.BaseTable,
          HasWhereCondition: !!whereCondition,
          WhereCondition: whereCondition
        }
      });

      if (result.Success && result.Results) {
        const values = result.Results.map(item => item[column.key]);
        return Array.from(new Set(values)).filter(v => v != null).sort();
      } else {
        console.error('Failed to fetch distinct values:', result.ErrorMessage);
        return [];
      }
    } catch (error) {
      console.error('Error fetching distinct values:', error);
      return [];
    }
  }

  /**
   * Task 2.4: Get distinct values with formatting
   */
  /**
   * Retrieves distinct values for a column with both raw and formatted representations.
   * This is the core method for populating dropdown filter options.
   *
   * @param column - The column to get distinct values for
   * @returns Promise resolving to array of DistinctValue objects with raw and formatted values
   *
   * @example
   * ```typescript
   * const statusColumn = { key: 'status', dropdownFilter: true };
   * const values = await this.getDistinctValuesFormatted(statusColumn);
   * // Returns: [{ raw: 'active', formatted: 'Active' }, { raw: 'inactive', formatted: 'Inactive' }]
   *
   * const priorityColumn = {
   *   key: 'priority',
   *   dropdownFilter: true,
   *   format: (val) => val === 1 ? 'High' : 'Low'
   * };
   * const values = await this.getDistinctValuesFormatted(priorityColumn);
   * // Returns: [{ raw: 1, formatted: 'High' }, { raw: 2, formatted: 'Low' }]
   * ```
   */
  async getDistinctValuesFormatted(column: DataGridColumn): Promise<DistinctValue[]> {
    // Special handling for Age column - return age ranges instead of distinct values
    if (column.key === 'Age' && column.dropdownFilter) {
      return this.getAgeRangeValues();
    }
    
    if (column.key === 'ChapterDuesBalance' && column.dropdownFilter) {
      return this.getChapterDuesBalanceRangeValues();
    }

    const rawValues = await this.getDistinctValuesRaw(column);

    // For complex formatters that need full item context, use different approach
    if (column.format && !column.formatForFilter) {
      return this.getFormattedDistinctValuesWithContext(column, rawValues);
    }

    // Simple formatting approach
    const distinctValues: DistinctValue[] = rawValues.map(rawValue => {
      // For boolean columns, keep the original raw values (0/1) but format for display
      return {
        raw: rawValue, // Keep original 0/1 values for filtering
        formatted: this.formatValueForDisplay(rawValue, column)
      };
    });

    // Sort by formatted value
    distinctValues.sort((a, b) => a.formatted.localeCompare(b.formatted));

    return distinctValues;
  }

  /**
   * Get age range values for Age column dropdown filter
   * Returns predefined age ranges: <20, 20-29, 30-39, 40-49, 50-59, 60-69, 70-79, >79
   */
  private getAgeRangeValues(): DistinctValue[] {
    return [
      { raw: '<20', formatted: '<20' },
      { raw: '20-29', formatted: '20-29' },
      { raw: '30-39', formatted: '30-39' },
      { raw: '40-49', formatted: '40-49' },
      { raw: '50-59', formatted: '50-59' },
      { raw: '60-69', formatted: '60-69' },
      { raw: '70-79', formatted: '70-79' },
      { raw: '>79', formatted: '>79' }
    ];
  }

  private getChapterDuesBalanceRangeValues(): DistinctValue[] {
    return [
      { raw: '0', formatted: '0' },
      { raw: '>0', formatted: '>0' }
    ];
  }

  /**
   * Build SQL filter for age range selection
   * Converts age range strings to SQL WHERE conditions
   * @param rangeValue - Age range string (e.g., '20-29', '<20', '>79')
   * @returns SQL WHERE condition for the age range
   */
  private buildAgeRangeFilter(rangeValue: string): string {
    switch (rangeValue) {
      case '<20':
        return 'Age < 20';
      case '20-29':
        return 'Age >= 20 AND Age <= 29';
      case '30-39':
        return 'Age >= 30 AND Age <= 39';
      case '40-49':
        return 'Age >= 40 AND Age <= 49';
      case '50-59':
        return 'Age >= 50 AND Age <= 59';
      case '60-69':
        return 'Age >= 60 AND Age <= 69';
      case '70-79':
        return 'Age >= 70 AND Age <= 79';
      case '>79':
        return 'Age > 79';
      default:
        // Fallback: treat as exact match
        return `Age = ${rangeValue}`;
    }
  }

  
  private buildChapterDuesBalanceFilter(rangeValue: string): string {
    switch (rangeValue) {
      case '0':
        return 'ChapterDuesBalance = 0';
      case '>0':
        return 'ChapterDuesBalance > 0';
      default:
        // Fallback: treat as exact match
        return `ChapterDuesBalance LIKE '%${rangeValue}%'`;
    }
  }

  /**
   * Task 2.8: Handle complex formatters requiring full item context
   */
  private async getFormattedDistinctValuesWithContext(column: DataGridColumn, rawValues: any[]): Promise<DistinctValue[]> {
    try {
      // Load sample data to get proper formatting context
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: this.entityName,
        ResultType: this.serverSide ? 'entity_object' : 'simple',
        ExtraFilter: this.buildCurrentFiltersForDistinct(column.key),
        MaxRows: 1000 // Limit for performance
      });

      if (result.Success && result.Results) {
        const valueMap = new Map<any, string>();

        // Build mapping from raw values to formatted values using actual data
        result.Results.forEach(item => {
          const rawValue = item[column.key];
          if (rawValue != null && !valueMap.has(rawValue)) {
            const formattedValue = this.getCellValue(item, column);
            valueMap.set(rawValue, formattedValue);
          }
        });

        // Create distinct values array from available mappings
        const distinctValues: DistinctValue[] = [];
        rawValues.forEach(rawValue => {
          const formatted = valueMap.get(rawValue) || this.formatValueForDisplay(rawValue, column);
          distinctValues.push({ raw: rawValue, formatted });
        });

        // Sort by formatted value
        distinctValues.sort((a, b) => a.formatted.localeCompare(b.formatted));
        return distinctValues;
      }
    } catch (error) {
      console.error('Error getting formatted distinct values with context:', error);
    }

    // Fallback to simple formatting
    return rawValues.map(rawValue => ({
      raw: rawValue,
      formatted: this.formatValueForDisplay(rawValue, column)
    }));
  }

  /**
   * Helper: Build filter string excluding the current column to avoid conflicts
   * Used for dropdown filter distinct value fetching
   */
  private buildCurrentFiltersForDistinct(excludeColumn: string): string {
    const filters: string[] = [];

    // Add committee access filtering (security first)
    if (this.enableCommitteeFiltering && this.serverSide) {
      const committeeFilter = this.buildCommitteeAccessFilter();
      if (committeeFilter) {
        filters.push(committeeFilter);
      }
    }

    // Add server-side filters
    if (this.serverSideFilters && this.serverSideFilters.length > 0) {
      const serverFilterSQL = this.buildServerSideFilterSQL();
      if (serverFilterSQL) {
        filters.push(serverFilterSQL);
      }
    }

    // Add current column filters (excluding the target column)
    this.currentFilters
      .filter(f => f.column !== excludeColumn)
      .forEach(filter => {
        const value = filter.value;
        if (value !== null && value !== undefined && value !== '') {
          // Special handling for Age column with range values
          if (filter.column === 'Age' && typeof value === 'string') {
            filters.push(this.buildAgeRangeFilter(value));
          } else if (filter.column === 'ChapterDuesBalance' && typeof value === 'string') {
            filters.push(this.buildChapterDuesBalanceFilter(value));
          } else {
            // Simple contains filter for other columns
            filters.push(`${filter.column} LIKE '%${value}%'`);
          }
        }
      });

    return filters.join(' AND ');
  }

  /**
   * Task 2.5: Get cached distinct values with TTL
   */
  async getCachedDistinctValues(column: DataGridColumn): Promise<DistinctValue[]> {
    const cacheKey = this.generateCacheKey(column);
    const cached = this.distinctValueCache.get(cacheKey);

    // Check if cache is valid
    if (cached && (Date.now() - cached.timestamp) < this.dropdownFilterCacheTimeout) {
      return cached.values;
    }

    // Fetch fresh values
    const values = await this.getDistinctValuesFormatted(column);

    // Update cache
    const filterHash = this.getCurrentFilterHash(column.key);
    this.distinctValueCache.set(cacheKey, {
      values,
      timestamp: Date.now(),
      filterHash
    });

    return values;
  }

  /**
   * Generate cache key for distinct values
   */
  private generateCacheKey(column: DataGridColumn): string {
    const filterHash = this.getCurrentFilterHash(column.key);
    return `${this.entityName}_${column.key}_${filterHash}`;
  }

  /**
   * Generate hash of current filters (excluding target column)
   */
  private getCurrentFilterHash(excludeColumn: string): string {
    const relevantFilters = this.currentFilters
      .filter(f => f.column !== excludeColumn)
      .map(f => `${f.column}:${f.value}`)
      .sort()
      .join('|');

    const serverFilterHash = this.serverSideFilters
      ? JSON.stringify(this.serverSideFilters)
      : '';

    return btoa(relevantFilters + serverFilterHash).substring(0, 16);
  }

  /**
   * Task 2.6: Get dropdown options for a column
   */
  async getColumnFilterOptions(column: DataGridColumn): Promise<DistinctValue[]> {
    if (!column.dropdownFilter) {
      return [];
    }

    // If static filterOptions are provided, return them directly
    if (column.filterOptions && column.filterOptions.length > 0) {
      return column.filterOptions;
    }

    // Check loading state
    const state = this.dropdownStates.get(column.key);
    if (state?.loading) {
      return state.options || [];
    }

    try {
      // Set loading state
      this.dropdownStates.set(column.key, {
        loading: true,
        error: null,
        options: []
      });

      const options = await this.getCachedDistinctValues(column);

      // Update state with results
      this.dropdownStates.set(column.key, {
        loading: false,
        error: null,
        options
      });

      return options;
    } catch (error) {
      // Update state with error
      const errorMessage = error instanceof Error ? error.message : 'Failed to load options';
      this.dropdownStates.set(column.key, {
        loading: false,
        error: errorMessage,
        options: []
      });

      console.error(`Error loading dropdown options for column ${column.key}:`, error);
      return [];
    }
  }

  /**
   * Task 2.7: Handle dropdown filter changes
   */
  async onDropdownFilter(column: DataGridColumn, event: Event): Promise<void> {
    const target = event.target as HTMLSelectElement;
    const selectedValue = target.value;

    // Update active dropdown filters
    if (selectedValue === '' || selectedValue === null || selectedValue === undefined) {
      this.activeDropdownFilters.delete(column.key);
    } else {
      this.activeDropdownFilters.set(column.key, selectedValue);
    }

    // Apply the filter using existing filter mechanism
    this.onFilter(column.key, selectedValue);
  }

  /**
   * Lazy load dropdown options when focused
   */
  async onDropdownFocus(column: DataGridColumn): Promise<void> {
    // Only load if not already loaded/loading
    const state = this.dropdownStates.get(column.key);
    if (!state || (!state.loading && state.options.length === 0 && !state.error)) {
      await this.getColumnFilterOptions(column);
    }
  }

  /**
   * Pre-load dropdown filter data for all columns to avoid loading delays
   */
  private async preloadDropdownFilters(): Promise<void> {
    if (!this.enableDropdownFilters || !this.columns) {
      return;
    }

    const dropdownColumns = this.columns.filter(col => col.dropdownFilter && col.filterable !== false);

    // Load all dropdown filters in parallel
    const loadPromises = dropdownColumns.map(async (column) => {
      try {
        await this.onDropdownFocus(column);
      } catch (error) {
        console.warn(`Failed to preload dropdown for column ${column.key}:`, error);
      }
    });

    await Promise.all(loadPromises);
  }

  /**
   * Check if column should show dropdown filter
   */
  /**
   * Determines whether to show a dropdown filter for a given column.
   * Checks both global enableDropdownFilters setting and column-specific dropdownFilter property.
   *
   * @param column - The column to check
   * @returns True if dropdown filter should be shown for this column
   *
   * @example
   * ```typescript
   * // Global dropdown filters enabled, column has dropdownFilter: true
   * this.enableDropdownFilters = true;
   * const column = { key: 'status', dropdownFilter: true };
   * const shouldShow = this.shouldShowDropdownFilter(column); // true
   *
   * // Global dropdown filters disabled
   * this.enableDropdownFilters = false;
   * const shouldShow = this.shouldShowDropdownFilter(column); // false
   *
   * // Column doesn't have dropdownFilter property
   * const textColumn = { key: 'name', filterable: true };
   * const shouldShow = this.shouldShowDropdownFilter(textColumn); // false
   * ```
   */
  shouldShowDropdownFilter(column: DataGridColumn): boolean {
    return !!(this.enableDropdownFilters && column.dropdownFilter && column.filterable !== false);
  }

  /**
   * Get dropdown loading state for a column
   */
  isDropdownLoading(column: DataGridColumn): boolean {
    // Static filterOptions don't need loading
    if (column.filterOptions && column.filterOptions.length > 0) {
      return false;
    }

    const state = this.dropdownStates.get(column.key);
    return state?.loading || false;
  }

  /**
   * Get dropdown error state for a column
   */
  getDropdownError(column: DataGridColumn): string | null {
    // Static filterOptions don't have errors
    if (column.filterOptions && column.filterOptions.length > 0) {
      return null;
    }

    const state = this.dropdownStates.get(column.key);
    return state?.error || null;
  }

  /**
   * Get dropdown options synchronously (for template use)
   */
  getDropdownOptions(column: DataGridColumn): DistinctValue[] {
    // If static filterOptions are provided, return them directly
    if (column.filterOptions && column.filterOptions.length > 0) {
      return column.filterOptions;
    }

    // Otherwise, return options from state (dynamically loaded)
    const state = this.dropdownStates.get(column.key);
    return state?.options || [];
  }

}